import { CnpjFormatter } from '@core/utils/cnpj.utils';
import {
  CnaeResponse,
  EnderecoEmpresaResponse,
} from '@interfaces/corporate-registry/responses/empresa-cadastral.response';
import { ErrorResponse } from '@models/commom/error-response';

/**
 * Formatação do cadastro da Receita, fiel ao Vault-Dossiê.
 *
 * Funções puras: quem formata data é o `DateFormatterService`, injetado como `FormatarData` —
 * a RF manda só data (`YYYY-MM-DD`), e parsear isso à mão em GMT-3 mostra o dia anterior.
 */
export type FormatarData = (isoDate: string) => string;

const vazio = (v: string | null | undefined): boolean => !v || !v.trim();
const digitos = (v: string): string => v.replace(/\D/g, '');

export function formatarCnae(c: CnaeResponse | null): string | null {
  if (!c) return null;
  return vazio(c.descricao) ? c.codigo : `${c.codigo} — ${c.descricao}`;
}

export function formatarEndereco(e: EnderecoEmpresaResponse): string | null {
  const partes = [e.logradouro, e.numero, e.complemento, e.bairro, e.municipio, e.uf].filter(
    p => !vazio(p)
  ) as string[];
  return partes.length ? partes.map(p => p.trim()).join(', ') : null;
}

export function formatarExterior(e: EnderecoEmpresaResponse): string | null {
  const paisEstrangeiro = !vazio(e.pais) && e.pais!.trim().toUpperCase() !== 'BRASIL';
  if (!paisEstrangeiro && vazio(e.cidadeExterior)) return null;
  return [e.cidadeExterior, paisEstrangeiro ? e.pais : null].filter(p => !vazio(p)).join(' — ');
}

export function formatarCep(cep: string | null): string | null {
  if (vazio(cep)) return null;
  const d = digitos(cep!);
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : cep!.trim();
}

export function formatarTelefone(t: string | null): string | null {
  if (vazio(t)) return null;
  const d = digitos(t!);
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return t!.trim();
}

export function formatarTelefones(ts: string[]): string | null {
  const ok = (ts ?? []).map(formatarTelefone).filter((t): t is string => !!t);
  return ok.length ? ok.join(' / ') : null;
}

export function formatarPeriodoSimples(
  optante: boolean,
  desde: string | null,
  saiu: string | null,
  fmt: FormatarData
): string {
  if (optante) return desde ? `Sim (desde ${fmt(desde)})` : 'Sim';
  if (!desde) return 'Não';
  return `Não (histórico) (desde ${fmt(desde)}${saiu ? ` — saiu em ${fmt(saiu)}` : ''})`;
}

export function motivoRelevante(motivo: string | null): string | null {
  return vazio(motivo) || /sem motivo/i.test(motivo!) ? null : motivo!.trim();
}

export function situacaoAtiva(situacao: string | null): boolean {
  return (situacao ?? '').trim().toLowerCase() === 'ativa';
}

/** CPF vem mascarado da RF e fica como está; CNPJ de sócio PJ vem cru e ganha máscara. */
export function formatarDocumentoSocio(doc: string | null): string | null {
  if (vazio(doc)) return null;
  return /^\d{14}$/.test(doc!.trim()) ? CnpjFormatter.format(doc!.trim()) : doc!.trim();
}

/** Igual ao `abreviar` do Dossiê: 29 caracteres + "..." (três pontos, não o caractere "…"). */
export function truncarRotulo(texto: string, max = 30): string {
  return texto.length > max ? `${texto.slice(0, max - 1)}...` : texto;
}

export type ErroRegistro =
  | { tipo: 'sem-permissao' | 'nao-encontrado' | 'indisponivel' }
  | { tipo: 'mensagem'; texto: string };

export const MENSAGEM_ERRO_REGISTRO: Record<
  'sem-permissao' | 'nao-encontrado' | 'indisponivel',
  string
> = {
  'sem-permissao': 'Seu perfil não tem acesso ao cadastro da Receita Federal.',
  'nao-encontrado': 'CNPJ não encontrado na base da Receita Federal.',
  indisponivel: 'A consulta à Receita está indisponível agora.',
};

/**
 * Traduz o erro do `VaultApiService` numa situação da tela.
 *
 * ⚠️ O serviço descarta o status HTTP: o que sobra é `ErrorResponse`. No ramo "Unknown error" ele
 * grava `"{status} : {mensagem}"`, e é daí que 403/404 são lidos. Formatos conferidos no staging
 * em 25/09/2026 (card 2989).
 */
export function classificarErroRegistro(err: unknown): ErroRegistro {
  if (!(err instanceof ErrorResponse)) return { tipo: 'indisponivel' };

  const erros = err.errors ?? [];
  const primeiro = erros[0];
  if (primeiro?.key === 'network') return { tipo: 'indisponivel' };

  const texto = primeiro?.message ?? '';
  if (/^403\b/.test(texto)) return { tipo: 'sem-permissao' };
  if (/^404\b/.test(texto)) return { tipo: 'nao-encontrado' };
  // O envelope de 404 do corporate-registry chega com `errors: []` e só a mensagem genérica.
  if (erros.length === 0) return { tipo: 'nao-encontrado' };
  // Validação (422) vem com `field` preenchido — ex.: "CNPJ inválido: informe 14 dígitos.".
  if (primeiro?.field && !vazio(primeiro.message))
    return { tipo: 'mensagem', texto: primeiro.message };
  return { tipo: 'indisponivel' };
}
